//AGENT_UPDATE_TYPES,

import { client, evictRootQueryCache } from '../../shared/apollo';
import { InsighttNotification } from '../../helpers/insightt-notification';

import { AppDispatch } from '../../redux/store';
import {
  CaseDetailInput,
  CaseDetailResponse,
  CasesActions,
  CasesEvent,
  FetchCaseDetailRdnActivityInput,
  FetchCaseDetailRdnActivityOutput,
  FetchCaseDetailStatusChangeInput,
  FetchCaseDetailStatusChangeOutput,
} from './type';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { SortOrder } from '@rapid-recovery-agency-inc/insightt-graphql';
import { normalizeMissedRepossessions } from './utils';
import {
  archiveCaseFromLists,
  assignCaseUsersUpdatesAction,
  fetchCaseDetail,
  fetchCaseUserActivity,
  fetchCaseDetailStatusChange,
  searchGraphqlError,
} from '@rapid-recovery-agency-inc/insightt-shared';
import gql from 'graphql-tag';
import { saveAssignedUsersAction } from '../repo-hit-list/repo-hit-list-actions';
import { APOLLO_ROOT_QUERY_FIELD_NAMES } from '../../constants/app.constants';

export const UPDATE_ONE_RHL = gql`
  mutation UpdateOneRDNCase($data: RDNCaseUpdateInput!, $where: RDNCaseWhereUniqueInput!) {
    updateOne: updateOneRDNCase(data: $data, where: $where) {
      caseId
    }
  }
`;

export const archiveCaseFromListsAction =
  (caseId: number, cb?: () => void) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      const { data } = await archiveCaseFromLists(client, caseId);
      InsighttNotification('success', 'Case deleted successfully');
      dispatch({
        type: CasesEvent.CASE_ARCHIVED_FROM_LIST,
        payload: data?.updateOne?.caseId,
      });
      if (cb) cb();
    } catch (error) {
      InsighttNotification('error', 'Error deleting the case.', error);
    }
  };

interface AssignUsersActionPayload {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  rdnCase: Record<string, any>;
  caseId: number;
  assignerId: number;
  usersAssigned: {
    id: number;
  }[];
  oldAssigned: {
    id: number;
    user: {
      id: number;
    };
  }[];
}

/**
 * To assign case to users.
 */
export const assignUsersAction =
  (data: AssignUsersActionPayload, callback?: () => unknown) =>
  async (dispatch: AppDispatch): Promise<void> => {
    //Clearing RDNCases avoids unassigned cases to re-appear as assigned when switching tabs in "Assigned to me"
    evictRootQueryCache(APOLLO_ROOT_QUERY_FIELD_NAMES.rDNCases);
    //Clearing aggregateRDNCase avoids red pill counter to show the wrong number of assigned cases after unassigning.
    evictRootQueryCache(APOLLO_ROOT_QUERY_FIELD_NAMES.aggregateRDNCase);
    dispatch({
      type: CasesEvent.CASE_ASSIGN_USERS,
      payload: { loading: true },
    });

    try {
      const res = await assignCaseUsersUpdatesAction(
        data.caseId,
        data.assignerId,
        data.usersAssigned,
        data.oldAssigned,
        data.rdnCase,
        client,
      );

      dispatch(
        saveAssignedUsersAction({
          caseId: data.caseId,
          usersAssigned: res.data?.updateOne?.usersAssigned as object[],
        }),
      );

      InsighttNotification('success', 'User assignation updated successfully!');
      dispatch({
        type: CasesEvent.CASE_ASSIGN_USERS,
        payload: { res, loading: false },
      });

      if (callback) {
        callback();
      }
    } catch (error) {
      const userAlreadyUnassigned = searchGraphqlError(
        error,
        'The records for relation `CaseAssigneeTocases` between the `RDNCase` and `CaseAssignee` models are not connected.',
      );

      dispatch({
        type: CasesEvent.CASE_ASSIGN_USERS,
        payload: { error, loading: false },
      });

      if (userAlreadyUnassigned) {
        InsighttNotification('error', 'User already unassigned');
      } else {
        InsighttNotification('error', 'Error assigning users.', error);
      }
    }
  };

export const fetchCaseDetailAction = createAsyncThunk<CaseDetailResponse, CaseDetailInput>(
  CasesActions.FETCH_CASE_DETAIL,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  async (caseId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await fetchCaseDetail(client, {
        caseId,
        spottedVehicleReportsOrderBy: SortOrder.DESC,
        missedRepossessionsOrderBy: SortOrder.DESC,
      });

      return fulfillWithValue({
        case: response.data.rDNCase,
        caseMissedRepossessions: normalizeMissedRepossessions(response.data.rDNCase?.missedRepossession),
      });
    } catch (err) {
      InsighttNotification('error', 'fetch case details error', err);
      return rejectWithValue(new Error(String(err)));
    }
  },
  {},
);

export const fetchCaseDetailStatusChangeAction = createAsyncThunk<
  FetchCaseDetailStatusChangeOutput,
  FetchCaseDetailStatusChangeInput
>(CasesActions.FETCH_CASE_STATUS_CHANGES, async ({ caseId, page }, { fulfillWithValue, rejectWithValue }) => {
  try {
    const response = await fetchCaseDetailStatusChange(client, {
      caseId,
      orderBy: SortOrder.DESC,
      skip: (page - 1) * 10,
      take: 10,
    });

    return fulfillWithValue({
      data: response.data.rDNCaseEvents,
      count: response.data.aggregateRDNCaseEvent._count?._all ?? 0,
    });
  } catch (err) {
    InsighttNotification('error', 'fetch case status change error', err);
    return rejectWithValue(new Error(String(err)));
  }
});

export const fetchCaseDetailRdnActivity = createAsyncThunk<
  FetchCaseDetailRdnActivityOutput,
  FetchCaseDetailRdnActivityInput
>(CasesActions.FETCH_CASE_RDN_ACTIVITY, async ({ caseId, page }, { fulfillWithValue, rejectWithValue }) => {
  try {
    const response = await fetchCaseUserActivity(client, {
      caseId,
      orderBy: SortOrder.DESC,
      skip: (page - 1) * 10,
      take: 10,
    });

    return fulfillWithValue({
      data: response.data.userActivities,
      count: response.data.aggregateUserActivity._count?._all ?? 0,
    });
  } catch (err) {
    InsighttNotification('error', 'fetch rdn activity error', err);
    return rejectWithValue(new Error(String(err)));
  }
});
